import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCLevelMetrics } from '../api/client'
import PageHeader, { Ico, StatCardModern, TodayPill } from '../components/PageHeader'
import { currentLocale } from '../i18n'

const PERIOD_IDS = ['week', 'month', 'quarter']

const STAGE_ORDER = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost']
const STAGE_COLOR = {
  new:        'bg-gray-300',
  contacted:  'bg-sky-300',
  qualified:  'bg-violet-400',
  proposal:   'bg-amber-400',
  won:        'bg-emerald-500',
  lost:       'bg-red-300',
}
const TIMEOFF_DOT = {
  PTO: 'bg-emerald-400',
  medical: 'bg-red-400',
  work_trip: 'bg-blue-400',
  admin: 'bg-amber-400',
}

function fmtHours(h) {
  if (h == null) return '0h'
  return `${Math.round(h).toLocaleString()}h`
}

function fmtMoney(byCurrency) {
  const entries = Object.entries(byCurrency || {})
  if (entries.length === 0) return '—'
  return entries.map(([c, v]) => `${Math.round(v).toLocaleString()} ${c}`).join('  ·  ')
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
}

// ── Period toggle (segmented control) ────────────────────────────────────────
function PeriodToggle({ value, onChange }) {
  const { t } = useTranslation('clevel')
  return (
    <div className="inline-flex p-0.5 bg-white border border-gray-200 rounded-lg">
      {PERIOD_IDS.map(id => {
        const active = id === value
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              active
                ? 'bg-gray-900 text-white font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t(`dashboard.periods.${id}`)}
          </button>
        )
      })}
    </div>
  )
}

// ── Card wrapper ─────────────────────────────────────────────────────────────
function Card({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
      {(title || subtitle) && (
        <div className="mb-4">
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  )
}

// ── Top projects + their upcoming milestones ────────────────────────────────
function TopProjectsAndMilestones({ projects, milestones }) {
  const { t } = useTranslation('clevel')
  if (!projects.length) {
    return <p className="text-sm text-gray-400">{t('dashboard.topProjects.noAllocations')}</p>
  }
  const max = Math.max(...projects.map(i => i.hours))
  const byProject = (milestones || []).reduce((acc, m) => {
    if (m.project_id == null) return acc
    if (!acc[m.project_id]) acc[m.project_id] = []
    acc[m.project_id].push(m)
    return acc
  }, {})

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      {projects.map(p => {
        const pct = max > 0 ? (p.hours / max) * 100 : 0
        const ms = byProject[p.project_id] || []
        return (
          <div key={p.project_id} className="border-b border-gray-100 last:border-0 md:border-b-0 pb-3 md:pb-0">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="w-1.5 h-5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">{p.nickname || p.name}</span>
              <span className="text-xs font-semibold text-gray-700 tabular-nums">{fmtHours(p.hours)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: p.color }}
              />
            </div>
            {ms.length === 0 ? (
              <p className="text-[11px] text-gray-400 ml-3">{t('dashboard.topProjects.noMilestones')}</p>
            ) : (
              <ul className="ml-3 space-y-1">
                {ms.slice(0, 3).map(m => (
                  <li key={m.id} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-700 flex-1 truncate">{m.name}</span>
                    <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full tabular-nums shrink-0">
                      {fmtDate(m.due_date)}
                    </span>
                  </li>
                ))}
                {ms.length > 3 && (
                  <li className="text-[11px] text-gray-400 ml-3">{t('dashboard.topProjects.moreCount', { count: ms.length - 3 })}</li>
                )}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── CRM pipeline — funnel-style stage list ───────────────────────────────────
function PipelineByStage({ byCount, byValue }) {
  const { t } = useTranslation('clevel')
  const total = STAGE_ORDER.reduce((s, k) => s + (byCount[k] || 0), 0)
  if (total === 0) {
    return <p className="text-sm text-gray-400">{t('dashboard.pipeline.noLeads')}</p>
  }
  return (
    <div className="space-y-2.5">
      {STAGE_ORDER.map(stage => {
        const count = byCount[stage] || 0
        const value = byValue[stage] || {}
        const pct = total > 0 ? (count / total) * 100 : 0
        return (
          <div key={stage}>
            <div className="flex items-center justify-between text-xs mb-1">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${STAGE_COLOR[stage]}`} />
                <span className="text-gray-700 font-medium">{t(`dashboard.stages.${stage}`)}</span>
                <span className="text-gray-400">{count}</span>
              </div>
              <span className="text-gray-500">{Object.keys(value).length ? fmtMoney(value) : ''}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${STAGE_COLOR[stage]}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── S-curve summary card ─────────────────────────────────────────────────────
function SCurveSummary({ data }) {
  const { t } = useTranslation('clevel')
  const planned = data.planned_to_date
  const actual = data.actual_to_date
  const variance = actual - planned
  const onTrack = Math.abs(variance) <= planned * 0.05
  const ahead = variance > 0
  const max = Math.max(planned, actual, 1)

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs text-gray-500">{t('dashboard.scurve.plannedToDate', { year: data.year })}</p>
          <p className="text-xl font-bold text-gray-900 tabular-nums mt-0.5">{fmtHours(planned)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">{t('dashboard.scurve.actualToDate')}</p>
          <p className="text-xl font-bold text-gray-900 tabular-nums mt-0.5">{fmtHours(actual)}</p>
        </div>
      </div>
      <div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gray-300"
            style={{ width: `${(planned / max) * 100}%` }}
            title={t('dashboard.scurve.titlePlanned', { hours: fmtHours(planned) })}
          />
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-1">
          <div
            className="h-full bg-indigo-500"
            style={{ width: `${(actual / max) * 100}%` }}
            title={t('dashboard.scurve.titleActual', { hours: fmtHours(actual) })}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] mt-1.5 text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-gray-300" /> {t('dashboard.scurve.legendPlanned')}</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-indigo-500" /> {t('dashboard.scurve.legendActual')}</span>
        </div>
      </div>
      <div className={`text-xs px-3 py-2 rounded-lg ${
        onTrack ? 'bg-emerald-50 text-emerald-700'
        : ahead ? 'bg-amber-50 text-amber-700'
        : 'bg-red-50 text-red-700'
      }`}>
        {onTrack
          ? t('dashboard.scurve.onPlan')
          : ahead
            ? t('dashboard.scurve.aheadOfPlan', { hours: fmtHours(Math.abs(variance)) })
            : t('dashboard.scurve.behindPlan', { hours: fmtHours(Math.abs(variance)) })}
      </div>
      <p className="text-[11px] text-gray-400">{t('dashboard.scurve.totalPlan', { year: data.year, hours: fmtHours(data.planned_total) })}</p>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function CLevelDashboard() {
  const { t } = useTranslation(['clevel', 'common'])
  const [period, setPeriod] = useState('week')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    getCLevelMetrics(period)
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || t('dashboard.failedToLoad')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [period])

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title={t('dashboard.header.title')}
        subtitle={t('dashboard.header.subtitle')}
        actions={
          <>
            <PeriodToggle value={period} onChange={setPeriod} />
            <TodayPill />
          </>
        }
      />

      {loading && !data && (
        <p className="text-sm text-gray-400">{t('dashboard.loading')}</p>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Period banner */}
          <div className="text-xs text-gray-500">
            {t('dashboard.periodLabel')} <span className="font-medium text-gray-700">{data.period.label}</span>
          </div>

          {/* Top KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCardModern
              icon={<Ico.Hours />}
              iconBg="bg-indigo-50" iconColor="text-indigo-600"
              label={t('dashboard.kpi.hoursAllocated')}
              value={fmtHours(data.engineering.total_hours)}
              sub={t('dashboard.kpi.capacitySub', {
                pct: data.engineering.capacity_pct.toFixed(0),
                capacity: fmtHours(data.engineering.capacity_hours),
              })}
            />
            <StatCardModern
              icon={<Ico.Folder />}
              iconBg="bg-blue-50" iconColor="text-blue-600"
              label={t('dashboard.kpi.activeProjects')}
              value={data.projects.active_count}
              sub={data.engineering.delayed_projects_count > 0
                ? t('dashboard.kpi.delayedSub', { count: data.engineering.delayed_projects_count })
                : t('dashboard.kpi.allOnSchedule')}
            />
            <StatCardModern
              icon={<Ico.Pipeline />}
              iconBg="bg-emerald-50" iconColor="text-emerald-600"
              label={t('dashboard.kpi.openLeads')}
              value={data.crm.open_count}
              sub={t('dashboard.kpi.totalLeadsSub', {
                total: data.crm.total_leads,
                wonCount: data.crm.period_won_count,
                periodKey: data.period.key,
              })}
            />
            <StatCardModern
              icon={<Ico.Users />}
              iconBg="bg-amber-50" iconColor="text-amber-600"
              label={t('dashboard.kpi.teamHeadcount')}
              value={data.team.headcount}
              sub={data.team.out_today.length > 0
                ? t('dashboard.kpi.outToday', { count: data.team.out_today.length })
                : t('dashboard.kpi.everyoneIn')}
            />
          </div>

          {/* Engineering row — top projects + their upcoming milestones */}
          <Card
            title={t('dashboard.topProjects.title')}
            subtitle={t('dashboard.topProjects.subtitle')}
          >
            <TopProjectsAndMilestones
              projects={data.engineering.top_projects}
              milestones={data.engineering.upcoming_milestones}
            />
          </Card>

          {/* Projects + CRM row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card
              title={t('dashboard.scurve.title')}
              subtitle={t('dashboard.scurve.subtitle', { year: data.projects.s_curve.year })}
            >
              <SCurveSummary data={data.projects.s_curve} />
            </Card>

            <Card
              title={t('dashboard.pipeline.title')}
              subtitle={t('dashboard.pipeline.subtitle')}
            >
              <PipelineByStage
                byCount={data.crm.by_stage_count}
                byValue={data.crm.by_stage_value}
              />
              <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('dashboard.pipeline.wonThisPeriod', { period: data.period.key })}</p>
                  <p className="text-base font-semibold text-emerald-600 mt-1 tabular-nums">{data.crm.period_won_count}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{fmtMoney(data.crm.period_won_value_by_currency)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('dashboard.pipeline.lostThisPeriod', { period: data.period.key })}</p>
                  <p className="text-base font-semibold text-red-500 mt-1 tabular-nums">{data.crm.period_lost_count}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Milestones + Team out row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card
              title={t('dashboard.milestones.title')}
              subtitle={t('dashboard.milestones.subtitle')}
            >
              {data.engineering.upcoming_milestones.length === 0 ? (
                <p className="text-sm text-gray-400">{t('dashboard.milestones.noMilestones')}</p>
              ) : (
                <div className="space-y-2">
                  {data.engineering.upcoming_milestones.map(ms => (
                    <div key={ms.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                      <div className="w-1.5 h-5 rounded-full shrink-0" style={{ backgroundColor: ms.project_color }} />
                      <span className="flex-1 text-sm text-gray-800 truncate">{ms.name}</span>
                      <span className="text-xs text-gray-400 max-w-32 truncate">{ms.project_name}</span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0 tabular-nums">
                        {fmtDate(ms.due_date)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card
              title={t('dashboard.teamOut.title')}
              subtitle={t('dashboard.teamOut.subtitle')}
            >
              {data.team.out_this_week.length === 0 ? (
                <p className="text-sm text-emerald-600">{t('dashboard.teamOut.everyoneIn')}</p>
              ) : (
                <div className="space-y-2">
                  {data.team.out_this_week.map((entry, i) => (
                    <div key={`${entry.team_member_id}-${i}`} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${TIMEOFF_DOT[entry.type] || 'bg-gray-300'}`} />
                      <span className="flex-1 text-sm font-medium text-gray-800 truncate">{entry.name}</span>
                      <span className="text-xs text-gray-500">{t(`dashboard.timeoff.${entry.type}`, { defaultValue: entry.type })}</span>
                      <span className="text-xs text-gray-400 tabular-nums shrink-0">
                        {fmtDate(entry.start_date)} – {fmtDate(entry.end_date)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
